import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  Label,
  FormGroup,
  Button,
  Badge,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import ComplaintDetailModal from "./helper/complaintDetailModal";
import SupportStatsModal from "./helper/supportStatsModal";
import EditComplaintModal from "./helper/editComplaintModal";
import { getSupportList, getSupportStats } from "./helper/supportApi";

const SupportPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [complaintModal, setComplaintModal] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [statsModal, setStatsModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedComplaintsForEdit, setSelectedComplaintsForEdit] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Filter states
  const [type, setType] = useState("");
  const [state, setState] = useState("");
  const [category, setCategory] = useState("");
  const [dateColumn, setDateColumn] = useState("createdAt");
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  // Fetch list function
  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
        type: type || undefined,
        state: state || undefined,
        category: category || undefined,
        dateColumn: dateColumn || undefined,
        dateRange: dateRange && !startDate && !endDate ? dateRange : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const result = await getSupportList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching support list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [type, state, category, dateColumn, dateRange, startDate, endDate, currentPage, limit]);

  // Handlers
  const handleViewUserInfo = useCallback((userId) => {
    if (!userId) return;
    setSelectedUserId(userId);
    setUserInfoModal(true);
  }, []);

  const handleViewComplaint = useCallback((complaint) => {
    setSelectedComplaint(complaint);
    setComplaintModal(true);
  }, []);

  const handleViewStats = useCallback(async () => {
    setStatsModal(true);
    setStatsLoading(true);
    setStats(null);

    try {
      const filters = {
        type: type || undefined,
        state: state || undefined,
        category: category || undefined,
        dateColumn: dateColumn || undefined,
        dateRange: dateRange && !startDate && !endDate ? dateRange : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      };

      const result = await getSupportStats(filters);
      setStats(result);
    } catch (error) {
      console.error("Error fetching support statistics:", error);
      alert(error.response?.data?.message || error.message || "Failed to fetch statistics");
    } finally {
      setStatsLoading(false);
    }
  }, [type, state, category, dateColumn, dateRange, startDate, endDate]);

  const handleEditComplaint = useCallback((complaint) => {
    setSelectedComplaintsForEdit([complaint]);
    setEditModal(true);
  }, []);

  const handleBulkEdit = useCallback(() => {
    if (selectedRows.length === 0) {
      alert("Please select at least one complaint to edit");
      return;
    }
    setSelectedComplaintsForEdit(selectedRows);
    setEditModal(true);
  }, [selectedRows]);

  const handleEditUpdate = useCallback(() => {
    // Refresh the list after update
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    setSelectedRows([]);
  }, [fetchList]);

  const getStateBadge = (stateValue) => {
    const stateLower = stateValue?.toLowerCase();
    switch (stateLower) {
      case "raised":
        return <Badge color="warning" className="px-2 py-1">Raised</Badge>;
      case "inprogress":
        return <Badge color="info" className="px-2 py-1">In Progress</Badge>;
      case "testing":
        return <Badge color="primary" className="px-2 py-1">Testing</Badge>;
      case "done":
        return <Badge color="success" className="px-2 py-1">Done</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{stateValue || "N/A"}</Badge>;
    }
  };

  const getTypeBadge = (typeValue) => {
    switch (typeValue) {
      case "Report bug":
        return <Badge color="danger" className="px-2 py-1">Report bug</Badge>;
      case "Suggest tag":
        return <Badge color="info" className="px-2 py-1">Suggest tag</Badge>;
      case "Feedback":
        return <Badge color="success" className="px-2 py-1">Feedback</Badge>;
      case "Help":
        return <Badge color="primary" className="px-2 py-1">Help</Badge>;
      default:
        return <Badge color="secondary" className="px-2 py-1">{typeValue || "N/A"}</Badge>;
    }
  };

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">ID</span>,
        selector: (row) => row.id,
        sortable: true,
        cell: (row) => (
          <div 
            className="font-monospace small text-primary cursor-pointer"
            style={{ cursor: "pointer", textDecoration: "underline" }}
            onClick={() => handleViewComplaint(row)}
            title="Click to view complaint details"
          >
            {row.id || "-"}
          </div>
        ),
        width: "80px",
      },
      {
        name: <span className="font-weight-bold fs-13">Type</span>,
        selector: (row) => row.complaintType || row.type,
        sortable: true,
        cell: (row) => getTypeBadge(row.complaintType || row.type),
      },
      {
        name: <span className="font-weight-bold fs-13">State</span>,
        selector: (row) => row.state,
        sortable: true,
        cell: (row) => getStateBadge(row.state),
      },
      {
        name: <span className="font-weight-bold fs-13">Category</span>,
        selector: (row) => row.category,
        sortable: true,
        cell: (row) => (
          <div className="small">{row.category || "Not Defined"}</div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Message</span>,
        selector: (row) => row.message,
        cell: (row) => (
          <div 
            className="text-truncate" 
            style={{ maxWidth: "300px" }}
            title={row.message}
          >
            {row.message || "-"}
          </div>
        ),
        wrap: true,
      },
      {
        name: <span className="font-weight-bold fs-13">User</span>,
        cell: (row) => {
          const user = row.user;
          if (!user) return "-";
          return (
            <div>
              <div 
                className="text-primary cursor-pointer"
                style={{ cursor: "pointer", textDecoration: "underline" }}
                onClick={() => handleViewUserInfo(user.userId)}
                title="Click to view user details"
              >
                {user.username || user.userId || "-"}
              </div>
              {user.userId && (
                <div className="text-muted small font-monospace">{user.userId}</div>
              )}
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Created At</span>,
        selector: (row) => row.created_at,
        sortable: true,
        cell: (row) => {
          const dateStr = row.created_at;
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          return <div className="small">{date.toLocaleString()}</div>;
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Updated At</span>,
        selector: (row) => row.updated_at,
        sortable: true,
        cell: (row) => {
          const dateStr = row.updated_at;
          if (!dateStr) return "-";
          const date = new Date(dateStr);
          return <div className="small">{date.toLocaleString()}</div>;
        },
      },
    ],
    [handleViewUserInfo, handleViewComplaint]
  );

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Fetch when filters change
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      setCurrentPage(1);
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, state, category, dateColumn, dateRange, startDate, endDate]);

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    switch (filterType) {
      case "type":
        setType(value);
        break;
      case "state":
        setState(value);
        break;
      case "category":
        setCategory(value);
        break;
      case "dateColumn":
        setDateColumn(value);
        break;
      case "dateRange":
        setDateRange(value);
        if (value !== "custom") {
          setStartDate("");
          setEndDate("");
        }
        break;
      case "startDate":
        setStartDate(value);
        if (value) setDateRange("custom");
        break;
      case "endDate":
        setEndDate(value);
        if (value) setDateRange("custom");
        break;
      default:
        break;
    }
  };

  const clearFilters = useCallback(() => {
    setType("");
    setState("");
    setCategory("");
    setDateColumn("createdAt");
    setDateRange("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Support" breadcrumbItem="Support" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-help-circle me-2"></i>Support
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label>Type</Label>
                        <Input
                          type="select"
                          value={type}
                          onChange={(e) => handleFilterChange("type", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="Report bug">Report bug</option>
                          <option value="Suggest tag">Suggest tag</option>
                          <option value="Feedback">Feedback</option>
                          <option value="Help">Help</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>State</Label>
                        <Input
                          type="select"
                          value={state}
                          onChange={(e) => handleFilterChange("state", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="raised">Raised</option>
                          <option value="inProgress">In Progress</option>
                          <option value="testing">Testing</option>
                          <option value="done">Done</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          value={category}
                          onChange={(e) => handleFilterChange("category", e.target.value)}
                        >
                          <option value="">All</option>
                          <option value="UI/UX Issues">UI/UX Issues</option>
                          <option value="Feature Not Working">Feature Not Working</option>
                          <option value="App Not Working Properly">App Not Working Properly</option>
                          <option value="New Feature Request">New Feature Request</option>
                          <option value="Account & Profile Issues">Account & Profile Issues</option>
                          <option value="Notifications & Alerts Issues">Notifications & Alerts Issues</option>
                          <option value="Third-Party Integrations Issues">Third-Party Integrations Issues</option>
                          <option value="Feedback & Suggestions">Feedback & Suggestions</option>
                          <option value="Others">Others</option>
                          <option value="Not Defined">Not Defined</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date Column</Label>
                        <Input
                          type="select"
                          value={dateColumn}
                          onChange={(e) => handleFilterChange("dateColumn", e.target.value)}
                        >
                          <option value="createdAt">Created At</option>
                          <option value="updatedAt">Updated At</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label>Date Range</Label>
                        <Input
                          type="select"
                          value={dateRange}
                          onChange={(e) => handleFilterChange("dateRange", e.target.value)}
                        >
                          <option value="">None</option>
                          <option value="last_24_hours">Last 24 Hours</option>
                          <option value="last_7_days">Last 7 Days</option>
                          <option value="last_30_days">Last 30 Days</option>
                          <option value="custom">Custom Range</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    {(dateRange === "custom" || (startDate || endDate)) && (
                      <>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Start Date</Label>
                            <Input
                              type="date"
                              value={startDate}
                              onChange={(e) => handleFilterChange("startDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>End Date</Label>
                            <Input
                              type="date"
                              value={endDate}
                              onChange={(e) => handleFilterChange("endDate", e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <div className="d-flex gap-2">
                        <Button color="info" onClick={handleViewStats} className="me-2">
                          <i className="mdi mdi-chart-bar me-1"></i>View Statistics
                        </Button>
                        <Button color="secondary" onClick={clearFilters}>
                          <i className="mdi mdi-filter-remove me-1"></i>Clear Filters
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Bulk Actions */}
                  {selectedRows.length > 0 && (
                    <div className="mb-3 p-3 bg-light rounded border">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <Badge color="primary" className="px-3 py-2 me-2">
                            {selectedRows.length} complaint{selectedRows.length !== 1 ? 's' : ''} selected
                          </Badge>
                        </div>
                        <div>
                          <Button
                            color="primary"
                            onClick={handleBulkEdit}
                            className="me-2"
                          >
                            <i className="mdi mdi-pencil me-1"></i>Bulk Edit
                          </Button>
                          <Button
                            color="secondary"
                            onClick={() => setSelectedRows([])}
                            size="sm"
                          >
                            <i className="mdi mdi-close me-1"></i>Clear Selection
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    selectableRows
                    selectableRowsHighlight
                    onSelectedRowsChange={(state) => {
                      setSelectedRows(state.selectedRows);
                    }}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-help-circle-outline" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No complaints found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUserId(null);
        }}
        userId={selectedUserId}
        onUpdate={() => {
          // Optionally refresh the list after user action
          if (hasInitialFetchedRef.current) {
            fetchList();
          }
        }}
      />

      <ComplaintDetailModal
        isOpen={complaintModal}
        toggle={() => {
          setComplaintModal(false);
          setSelectedComplaint(null);
        }}
        complaint={selectedComplaint}
        onViewUser={handleViewUserInfo}
        onEdit={handleEditComplaint}
      />

      <SupportStatsModal
        isOpen={statsModal}
        toggle={() => {
          setStatsModal(false);
          setStats(null);
        }}
        stats={stats}
        loading={statsLoading}
      />

      <EditComplaintModal
        isOpen={editModal}
        toggle={() => {
          setEditModal(false);
          setSelectedComplaintsForEdit([]);
        }}
        complaints={selectedComplaintsForEdit}
        onUpdate={handleEditUpdate}
      />
    </React.Fragment>
  );
};

export default SupportPage;

